import React, { useState } from 'react'
import Icon from 'components/Icon'
import VoiceCompact from 'components/VoiceCompact'
import ChildVoiceCard from 'components/ChildVoiceCard'
import classNames from 'classnames'
import { FormattedMessage, FormattedList, useIntl } from 'react-intl'
import { Button, Banner, Card } from '@arnsbogroup/react-voicearchive'
import { Link } from 'react-router-dom'
import languageNames from 'data/languages/messages'
import genderNames from 'data/genders/messages'
import Price from 'containers/Price'
import messages from './messages'
import useCurrentUser from 'hooks/useCurrentUser'
import { intents } from 'contexts/ProjectContext'
import { messages as routeMessages } from 'routes'

const VoiceDrawer = ({ project, dispatch }) => {
  const { formatMessage } = useIntl()
  const [expanded, setExpanded] = useState(false)
  const { roles = [] } = useCurrentUser()

  return (
    <div
      className={classNames(
        project.isScoped
          ? ['z-50', 'md:w-1/4', 'h-auto']
          : ['w-full md:w-0', 'h-0 md:h-full'],
        [
          'bg-neutral-50',
          'border-gray-300',
          'border-t',
          'bottom-0',
          'delay-150',
          'ease-in-out',
          'fixed',
          'flex',
          'flex-col',
          'max-h-full',
          'md:border-l',
          'md:right-0',
          'md:top-0',
          'overflow-hidden',
          'overflow-hidden',
          'shadow-lg',
          'transition-all',
          'w-full',
        ]
      )}
    >
      <div
        className={classNames(
          'md:flex-grow overflow-y-scroll p-4 space-y-4',
          expanded ? 'block' : 'hidden md:block'
        )}
      >
        <div className="flex items-center justify-between">
            <p className="font-medium text-sm text-gray-600">
              { project.hash ? (
                <span><FormattedMessage {...messages.titleHash} values={{hashId: project.hashId}}/></span>
              ) : project.intent == intents.DOWNLOAD ? (
                <span><FormattedMessage {...messages.titleGroupBy}/></span>
              ) : (
                <span><FormattedMessage {...messages.titleDefault}/></span>
              ) }
            </p>

          <Button
            variant="inline"
            className="px-1 text-sm hover:text-negative"
            onClick={() => {
              if (window.confirm(formatMessage(messages.startOverPrompt))) {
                project.reset()
              }
            }}
            icon="times"
            iconPosition="right"
          >
            <FormattedMessage {...messages.reset }/>
          </Button>
        </div>

        { project.isLocallyStored && !project.changed &&
          <Banner
            state="success"
            subtitle={formatMessage(messages.isLocallyStored)}
          />
        }

        { Object.values(project.voices || {}).some(({ age }) => age && age < 18) && !roles.includes('coordinator') &&
          <ChildVoiceCard/>
        }

        { roles.includes('coordinator') && project.leadId && (
          <Banner
            state="info"
            subtitle={formatMessage(messages.preparingProjectLead, {leadId: project.leadId})}
          />
        )}

        { roles.includes('coordinator') && project.dealId && (
          <Banner
            state="info"
            subtitle={formatMessage(messages.preparingProjectDeal, {dealId: project.dealId})}
          />
        )}

        {!!project.categories.length && (
          <Banner
            state="info"
            subtitle={formatMessage(messages.autoSelectionDems, {categories: project.categories.join(', ')})}
          />
        )}

        {project.includeAll ? (
          <div className="space-y-4">
            <Banner
              state="info"
              subtitle={formatMessage(messages.includesAllVoices)}
            />

            {[
              ['Languages', project.languages, languageNames],
              ['Genders', project.genders, genderNames],
            ]
              .filter(([, items]) => !!items.length)
              .map(([key, items, messages], i) => (
                <div key={key}>
                  <p className="text-medium text-gray-600 text-sm">{key}</p>
                  <FormattedList
                    type="conjunction"
                    value={items.map((id) => id in messages ? formatMessage(messages[id]) : id )}
                  />
                </div>
              ))}

            {!!project.collections.length && (
              <div>
                <p className="text-medium text-gray-600 text-sm">
                  Collections
                </p>
                <FormattedList
                  type="conjunction"
                  value={project.collections.map(({ name }) => name)}
                />
              </div>
            )}
          </div>
        ) : (
          <GroupedVoices voices={project.voices} dispatch={dispatch} />
        )}

        { project.intent == intents.DOWNLOAD ? (
          <Card className="block border-dashed p-2 text-sm text-gray-600 space-y-2">
            <p><FormattedMessage {...messages.downloadingByClickingBelow}/></p>
          </Card>
        ) : !roles.includes('coordinator') ? (
          <Card className="block border-dashed p-2 text-sm text-gray-600 space-y-2">
              <FormattedMessage {...messages.callToAction} values={{
                  h1: chunks => <p className="font-medium">{chunks}</p>,
                  p: chunks => <p>{chunks}</p>
              }}/>
          </Card>
        ) : (
          null
        ) }
      </div>

      <button
        className="md:hidden text-primary font-medium py-3"
        onClick={() => setExpanded(!expanded)}
      >
        <FormattedMessage
          {...messages[expanded ? 'hide' : 'show']}
          values={{ count: Object.keys(project.voices || {}).length }}
        />

        <Icon icon={expanded ? 'arrow-down' : 'arrow-up'} />
      </button>


      <div className="flex-shrink-0 p-4 space-y-2">
        {!roles.includes('coordinator') && (
          <>
            <div className="hidden md:block md:pb-2">
              <Price
                onRequest={false}
                className="text-base font-medium text-gray-600"
                showPerVoiceTalentLabel
                autoHide
              />
            </div>
            <div
              className={classNames(
                'space-y-1 text-md text-gray-600 pb-4',
                expanded ? 'block' : 'hidden md:block'
              )}
            >
              <div className="flex items-start space-x-2">
                <Icon icon="check" className="text-positive mt-0.5 flex-shrink-0" />
                <span><FormattedMessage {...messages.uspQuoteSpeed} /></span>
              </div>
              <div className="flex items-start space-x-2">
                <Icon icon="check" className="text-positive mt-0.5 flex-shrink-0" />
                <span><FormattedMessage {...messages.uspTransparentPrices} /></span>
              </div>
              <div className="flex items-start space-x-2">
                <Icon icon="check" className="text-positive mt-0.5 flex-shrink-0" />
                <span><FormattedMessage {...messages.uspExclusivePartnerships} /></span>
              </div>
            </div>
          </>
        )}

        { project.loading ? (
          <Button variant="outline" disabled className="w-full">
            <span className="space-x-1">
              <Icon icon="spinner-third" className="animate-spin" />
              <span><FormattedMessage {...messages.loading} /></span>
            </span>
          </Button>
        ) : project.id && project.changed ? (
          <Button icon="save" className="w-full" onClick={() => project.save()}>
            Save changes
          </Button>
        ) : project.id && roles.includes('coordinator') ? (
          <Link to={`${formatMessage(routeMessages.project).replace('/*', '')}/${project.hashId}`}>
            <Button className="w-full">Back to project</Button>
          </Link>
        ) : roles.includes('coordinator') ? (
          <Link to={formatMessage(routeMessages.project).replace('/*', '')} state={{ action: intents.GENERATE }} className="">
            <Button className="w-full">Generate</Button>
          </Link>
        ) : project.includeAll ? (
          <Link to={formatMessage(routeMessages.project).replace('/*', '')} state={{ action: intents.SHARE }} className="">
            <Button className="w-full">Share search results</Button>
          </Link>
        ) : project.intent == intents.DOWNLOAD ? (
          <Link to={formatMessage(routeMessages.project).replace('/*', '')} state={{ action: intents.DOWNLOAD }} className="">
            <Button className="w-full" icon="download">Download demos</Button>
          </Link>
        ) : (
          <Link to={formatMessage(routeMessages.project).replace('/*', '')} state={{ action: intents.QUOTE }} className="">
            <Button className="w-full">Get free quote</Button>
          </Link>
        )}

        {!roles.includes('coordinator') && (
          <div className="flex space-x-2">
            <div className={project.includeAll ? 'w-full': 'w-1/2'}>
              { project.id && project.changed ? (
                <Link to="/project" state={{ action: 'QUOTE' }} className="">
                  <Button variant="outline" size="sm" className="w-full"><FormattedMessage {...messages.freeQuote} /></Button>
                </Link>
              ) : project.id && !project.changed ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled
                  icon="check"
                >
                  <FormattedMessage {...messages.save}/>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => project.create(true)}
                  icon="save"
                >
                  <FormattedMessage {...messages.saveLater}/>
                </Button>
              ) }
            </div>

            { !project.includeAll &&
              <Link
                to={formatMessage(routeMessages.project).replace('/*', '')}
                state={{ action: 'SHARE' }}
                className="w-1/2 2xl:w-1/2"
              >
                <Button variant="outline" size="sm" className="w-full">
                  <Icon icon="external-link" /> <FormattedMessage {...messages.share}/>
                </Button>
              </Link>
            }
          </div>
        )}
      </div>
    </div>
  )
}

const GroupedVoices = ({ voices = {}, dispatch }) => {
  const voiceMap = Object.values(voices).reduce((memo, voice, i) => {
    voice.languages?.forEach((langTag) => {
      if (!Array.isArray(memo[langTag])) {
        memo[langTag] = []
      }

      memo[langTag].push(voice)
    })

    return memo
  }, {})

  const characterMap = Object.values(voices)
    .filter((voice) => voice?.character)
    .reduce((memo, voice) => {
      const character = voice.character;

      if (!Array.isArray(memo[character])) {
        memo[character] = [];
      }

      memo[character].push(voice?.sku);
      return memo;
    }, {});

  return (
    <div className="space-y-4">
      {Object.entries(characterMap).length > 0 &&
        <div className='bg-white border rounded p-2'>
          <p className="font-medium text-sm text-gray-600">
            <FormattedMessage {...messages.characters} tagName="span" />
          </p>
          {Object.entries(characterMap).map(([key, voices]) =>
            <div key={key} className="space-1">
              <div className="flex items-center justify-between">
                <p className="text-gray-600 text-sm font-medium space-x-1">
                  <span>{ key }</span>
                  <span>({voices.length})</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {voices.map((voice) => (
                  <span className='text-xs'>{voice}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      {Object.entries(voiceMap).map(([key, voices]) => (
        <div key={key} className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="px-1 text-gray-600 text-sm font-medium space-x-1">
              { key in languageNames ? (
                <FormattedMessage {...languageNames[key]} tagName="span" />
              ) : (
                <span>{ key }</span>
              ) }

              <span>({voices.length})</span>
            </p>
          </div>

          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2">
            {voices.map((voice) => (
              <VoiceCompact
                key={`${key}-${voice.id}`}
                voice={voice}
                onDeselect={() =>
                  dispatch({ type: 'REMOVE_VOICE', payload: voice })
                }
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default VoiceDrawer
